import { z } from 'zod';

import { baseColors } from '@/warranty/view/components/color-palette';

export enum ConditionViolationType {
  HIGH_AVG_SOC = 'HIGH_AVG_SOC',
  REST_SOC = 'REST_SOC',
  EXCESSIVE_CYCLING = 'EXCESSIVE_CYCLING',
  LARGE_DOD = 'LARGE_DOD',
  THROUGHPUT = 'THROUGHPUT',
  TEMPERATURE_SPIKE = 'TEMPERATURE_SPIKE',
  HIGH_VOLTAGE = 'HIGH_VOLTAGE',
  COOLDOWN_INTERRUPTED = 'COOLDOWN_INTERRUPTED',
  C_RATE_HIGH = 'C_RATE_HIGH',
  C_RATE_LOW = 'C_RATE_LOW',
  CURRENT_HIGH = 'CURRENT_HIGH',
  CURRENT_LOW = 'CURRENT_LOW',
  EFC_HIGH = 'EFC_HIGH',
  POWER_HIGH = 'POWER_HIGH',
  POWER_LOW = 'POWER_LOW',
  SOC_HIGH_AVG = 'SOC_HIGH_AVG',
  SOC_HIGH = 'SOC_HIGH',
  SOC_LOW = 'SOC_LOW',
  TEMPERATURE_HIGH = 'TEMPERATURE_HIGH',
  TEMPERATURE_HIGH_AVG = 'TEMPERATURE_HIGH_AVG',
  TEMPERATURE_LOW = 'TEMPERATURE_LOW',
  VOLTAGE_HIGH = 'VOLTAGE_HIGH',
  VOLTAGE_LOW = 'VOLTAGE_LOW',
  NO_DATA = 'NO_DATA',
  UNKNOWN = 'UNKNOWN',
}

export const ConditionViolationLabel: Record<ConditionViolationType, string> = {
  [ConditionViolationType.HIGH_AVG_SOC]: 'High avg SOC',
  [ConditionViolationType.REST_SOC]: 'Rest SOC',
  [ConditionViolationType.EXCESSIVE_CYCLING]: 'Excessive Cycling',
  [ConditionViolationType.LARGE_DOD]: 'Large DOD',
  [ConditionViolationType.THROUGHPUT]: 'Throughput',
  [ConditionViolationType.TEMPERATURE_SPIKE]: 'Temperature Spike',
  [ConditionViolationType.HIGH_VOLTAGE]: 'High Voltage',
  [ConditionViolationType.NO_DATA]: 'No Data',
  [ConditionViolationType.UNKNOWN]: 'Unknown Condition',
  [ConditionViolationType.COOLDOWN_INTERRUPTED]: 'Cooldown interrupted',
  [ConditionViolationType.C_RATE_HIGH]: 'High C-rate charge',
  [ConditionViolationType.C_RATE_LOW]: 'High C-rate discharge',
  [ConditionViolationType.CURRENT_HIGH]: 'High current charge',
  [ConditionViolationType.CURRENT_LOW]: 'High current discharge',
  [ConditionViolationType.EFC_HIGH]: 'Too many cycles',
  [ConditionViolationType.POWER_HIGH]: 'High power charge',
  [ConditionViolationType.POWER_LOW]: 'High power discharge',
  [ConditionViolationType.SOC_HIGH_AVG]: 'High avg state of charge',
  [ConditionViolationType.SOC_HIGH]: 'High state of charge',
  [ConditionViolationType.SOC_LOW]: 'Low state of charge',
  [ConditionViolationType.TEMPERATURE_HIGH]: 'High temperature',
  [ConditionViolationType.TEMPERATURE_HIGH_AVG]: 'High avg temperature',
  [ConditionViolationType.TEMPERATURE_LOW]: 'Low temperature',
  [ConditionViolationType.VOLTAGE_HIGH]: 'High voltage',
  [ConditionViolationType.VOLTAGE_LOW]: 'Low voltage',
};

export const ConditionViolationsStatusSchema = z.object({
  id: z.string(),
  groupFullId: z.string(),
  topFullId: z.string(),
  unitFullId: z.string().optional(),
  fullFrontendId: z.string(),
  description: z.nativeEnum(ConditionViolationType),
  extremeValue: z.number(),
  unit: z.string(),
  latestDetect: z.date(),
  duration: z.number(),
  affectedUnits: z.number(),
});

export type ConditionViolationsStatus = z.infer<
  typeof ConditionViolationsStatusSchema
>;

export const ConditionViolationColors: Record<ConditionViolationType, string> =
  {
    [ConditionViolationType.HIGH_AVG_SOC]: baseColors[0], // orange
    [ConditionViolationType.LARGE_DOD]: baseColors[4], // green
    [ConditionViolationType.TEMPERATURE_SPIKE]: baseColors[8], // blue_pastel
    [ConditionViolationType.NO_DATA]: baseColors[10], // gray_pale
    [ConditionViolationType.EXCESSIVE_CYCLING]: baseColors[2], // gold_saturated
    [ConditionViolationType.HIGH_VOLTAGE]: baseColors[3], // green_pale
    [ConditionViolationType.UNKNOWN]: baseColors[16], // black
    [ConditionViolationType.REST_SOC]: baseColors[1], // yellow
    [ConditionViolationType.THROUGHPUT]: baseColors[5], // green_dark
    [ConditionViolationType.COOLDOWN_INTERRUPTED]: baseColors[6],
    [ConditionViolationType.C_RATE_HIGH]: baseColors[7],
    [ConditionViolationType.C_RATE_LOW]: baseColors[7],
    [ConditionViolationType.CURRENT_HIGH]: baseColors[9],
    [ConditionViolationType.CURRENT_LOW]: baseColors[9],
    [ConditionViolationType.EFC_HIGH]: baseColors[2],
    [ConditionViolationType.POWER_HIGH]: baseColors[11],
    [ConditionViolationType.POWER_LOW]: baseColors[11],
    [ConditionViolationType.SOC_HIGH_AVG]: baseColors[0],
    [ConditionViolationType.SOC_HIGH]: baseColors[0],
    [ConditionViolationType.SOC_LOW]: baseColors[1],
    [ConditionViolationType.TEMPERATURE_HIGH]: baseColors[8],
    [ConditionViolationType.TEMPERATURE_HIGH_AVG]: baseColors[8],
    [ConditionViolationType.TEMPERATURE_LOW]: baseColors[9],
    [ConditionViolationType.VOLTAGE_HIGH]: baseColors[3],
    [ConditionViolationType.VOLTAGE_LOW]: baseColors[3],
  };
